import React from "react";
import { observer } from "mobx-react";
import useStore from "../../helpers/useStore";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";
import { useSaveAppointments } from "../../stores/Appointment/appointment.mutation";
import CAResponseDialog from "./CAResponseDialog";
import AppointmentNotes from "../../molecules/Calendar/AppointmentNotes";
import ClosestAppointmentsModal from "../../molecules/Calendar/ClosestAppointmentsModal";
import TemplateScheduleDialog from "../../molecules/Calendar/DynamicTemplateProvider/TemplateScheduleDialog";
import CalendarActionDialogs from "./CalendarActionDialogs";

/**
 * SchedulerDialogs component manages all dialog state for the Scheduler interface.
 * It observes the CalendarStore for dialog state changes and renders appropriate dialogs.
 * This centralizes dialog management and avoids prop drilling through multiple components.
 * 
 * Current dialogs:
 * - CAResponseDialog: Handles continuous appointment responses
 */
const SchedulerDialogs: React.FC = observer(() => {
  const CalendarStore = useStore(CalendarContext);
  const { mutateAsync: saveAppointments } = useSaveAppointments();

  const { caResponseDialogOpen, caResponseData, closeCAResponseDialog, notesDialogOpen, notesDialogAppointmentId, closeNotesDialog } = CalendarStore;

  return (
    <>
      {/* CAResponse Dialog for handling continuous appointment responses */}
      <CAResponseDialog
        open={caResponseDialogOpen}
        handleClose={closeCAResponseDialog}
        responseDialogData={caResponseData}
        saveAppointments={(appointments) => saveAppointments({ appointmentList: appointments })}
      />
      
      {/* Notes Dialog for appointment notes */}
      {notesDialogOpen && notesDialogAppointmentId && (
        <AppointmentNotes
          appointmentId={notesDialogAppointmentId}
          showDialog={notesDialogOpen}
          onClose={closeNotesDialog}
        />
      )}
      
      {/* Closest Appointments Modal for heatmap feature */}
      <ClosestAppointmentsModal />

      {/* Missing-data dialog for dynamic template drops (MD-148) */}
      <TemplateScheduleDialog />

      {/* Action-dialog host: renders CalendarStore.activeCalendarDialog (the
          context-menu / hotkey action system's dialogs). */}
      <CalendarActionDialogs />
    </>
  );
});

SchedulerDialogs.displayName = "SchedulerDialogs";

export default SchedulerDialogs;